import { resyncTag } from './navigation';
import { clientCounts, recordTotals, warningTally } from './report-totals';
import { formatWhen, plural } from './report-values';

import type { FinalReport } from './final-report';
import type { ModuleReportView } from './module-report-types';

/**
 * The top of the final report — title, the one-sentence lede, the meta strip,
 * the summary strip, the footer — as copy built from the data. Pure, so every
 * sentence an auditor reads is pinned by a test, degraded inputs included.
 */

export function reportEyebrow(report: FinalReport): string {
    const kind = report.header.isFinal ? 'Final report' : 'Partial report';
    return `Module ${report.header.moduleTitle} · ${kind}`;
}

export function reportTitle(report: FinalReport): string {
    return `${report.header.moduleTitle} migration — Salesforce → Veevart 2.0`;
}

function listJoin(items: readonly string[]): string {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] ?? ''}`;
}

function joined(parts: readonly (string | null)[]): string {
    return parts.filter((part): part is string => part !== null).join(', ');
}

const part = (count: number, words: string) => (count > 0 ? `${String(count)} ${words}` : null);

function clientsClause(report: FinalReport): string {
    const counts = clientCounts(report);
    if (counts.selected === 0) return 'No client was selected for this migration.';
    const extra = joined([
        part(counts.failed, 'failed'),
        part(counts.blocked, 'blocked'),
        part(counts.errored, counts.errored === 1 ? 'ended in an error' : 'ended in errors'),
        part(counts.skipped, 'skipped'),
        part(counts.running, 'still running'),
        part(counts.unreadable, 'with an unreadable status'),
    ]);
    const lead = `${String(counts.completed)} of ${plural(counts.selected, 'selected client')} completed`;
    return `${extra.length > 0 ? `${lead}; ${extra}` : lead}.`;
}

function warningsClause(report: FinalReport, view: ModuleReportView): string {
    const tally = warningTally(report);
    if (tally.total === 0) return '';
    const kind =
        tally.blocking === 0
            ? `${plural(tally.total, 'non-blocking warning')}`
            : `${plural(tally.total, 'warning')} (${String(tally.blocking)} blocking)`;
    const topics = [
        ...new Set(
            tally.codes
                .map((code) => view.warningTopic(code))
                .filter((topic): topic is string => topic !== null),
        ),
    ];
    return `; ${kind}${topics.length > 0 ? ` about ${listJoin(topics)}` : ''}`;
}

/** The one narrative paragraph under the title. */
export function reportLede(report: FinalReport, view: ModuleReportView): string {
    const clients = clientsClause(report);
    if (clientCounts(report).selected === 0) return clients;
    const totals = recordTotals(report);
    if (totals.discovered === 0) {
        return `${clients} No ${view.recordNoun} has been read from a run yet.`;
    }
    const rest = joined([
        part(totals.failed, 'failed'),
        part(totals.skipped, 'skipped'),
        part(totals.blocked, 'blocked'),
        part(totals.inProgress, 'still in progress'),
    ]);
    const failures = rest.length === 0 ? ' with no failures' : `; ${rest}`;
    const records = `Migrated ${String(totals.migrated)} of ${plural(totals.discovered, view.recordNoun)}${failures}`;
    return `${clients} ${records}${warningsClause(report, view)}.`;
}

export interface MetaFact {
    readonly label: string;
    readonly value: string;
    readonly mono?: boolean;
}

export function reportMeta(report: FinalReport): MetaFact[] {
    const { header } = report;
    const { dryRun, launch, lastRefresh } = header;
    const reason = launch.reason ?? dryRun?.reason ?? null;
    const resync = resyncTag(launch);
    const facts: (MetaFact | null)[] = [
        { label: 'Generated', value: formatWhen(header.generatedAt) },
        header.environment === null ? null : { label: 'Environment', value: header.environment },
        dryRun === null ? null : { label: 'Requested by', value: dryRun.requestedBy },
        reason === null ? null : { label: 'Reason', value: reason },
        dryRun === null ? null : { label: 'Dry-run', value: dryRun.reportId, mono: true },
        {
            label: 'Launch',
            value: `${launch.requestedBy} · ${formatWhen(launch.finishedAt ?? launch.createdAt)}`,
        },
        resync === null ? null : { label: 'Mode', value: resync },
        {
            label: 'Last refreshed',
            value:
                lastRefresh !== null
                    ? formatWhen(lastRefresh.finishedAt ?? lastRefresh.createdAt)
                    : header.refreshLookupLimited
                      ? 'not found among the recent jobs'
                      : 'never',
        },
        {
            label: 'Sandboxes',
            value: (dryRun ?? launch).excludeSandboxes ? 'excluded' : 'included',
        },
        {
            label: 'State',
            value: header.isFinal
                ? 'final'
                : `partial · ${plural(header.unsettled, 'client')} unsettled`,
        },
    ];
    return facts.filter((fact): fact is MetaFact => fact !== null);
}

export type CellTone = 'default' | 'success' | 'warning' | 'danger';

export interface SummaryCell {
    readonly key: string;
    readonly value: number;
    /** Shown smaller after the value (`12/14`); `null` for a plain count. */
    readonly total: number | null;
    /** A short muted aside after the value ("0 blocking"); `null` for none. */
    readonly aside: string | null;
    readonly label: string;
    readonly tone: CellTone;
}

export function summaryCells(report: FinalReport): SummaryCell[] {
    const counts = clientCounts(report);
    const totals = recordTotals(report);
    const tally = warningTally(report);
    const problems = totals.failed + totals.skipped + totals.blocked;
    const allDone = (done: number, of: number): CellTone =>
        of > 0 && done === of ? 'success' : 'default';
    const cell = (
        key: string,
        value: number,
        label: string,
        tone: CellTone,
        extra: Partial<Pick<SummaryCell, 'total' | 'aside'>> = {},
    ): SummaryCell => ({ key, value, label, tone, total: null, aside: null, ...extra });
    return [
        cell(
            'clients',
            counts.completed,
            'Clients completed',
            allDone(counts.completed, counts.selected),
            { total: counts.selected },
        ),
        cell(
            'records',
            totals.migrated,
            'Records migrated',
            allDone(totals.migrated, totals.discovered),
            { total: totals.discovered },
        ),
        ...(totals.inProgress > 0
            ? [cell('in-progress', totals.inProgress, 'Records in progress', 'default')]
            : []),
        cell(
            'problems',
            problems,
            totals.blocked > 0 ? 'Failed, skipped or blocked' : 'Failed or skipped',
            problems > 0 ? 'danger' : 'default',
        ),
        cell('warnings', tally.total, 'Warnings', tally.total > 0 ? 'warning' : 'default', {
            aside: `${String(tally.blocking)} blocking`,
        }),
        cell('not-migrated', counts.notMigrated, 'Not migrated', 'default'),
    ];
}

/** The footer: which jobs the report reads, and where the runner exported them. */
export function reportFooter(report: FinalReport): string[] {
    const { dryRun, launch, lastRefresh } = report.header;
    const source = lastRefresh ?? launch;
    const based = [
        source === launch ? null : `launch ${launch.reportId}`,
        dryRun === null ? null : `dry-run ${dryRun.reportId}`,
    ].filter((part): part is string => part !== null);
    return [
        based.length === 0
            ? `Report ${source.reportId}`
            : `Report ${source.reportId} · based on ${based.join(' / ')}`,
        ...(source.exportLocation === null ? [] : [`Export: ${source.exportLocation}`]),
    ];
}
